import { Button, Text } from '@chakra-ui/react'
import { useMutation } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { Link as RouterLink } from 'react-router'
import { api, isApiError } from '~/api/client'
import { ActionBar } from '~/components/member/ActionBar'
import { isWeakPasswordError, PasswordField } from '~/components/member/PasswordField'
import { ErrorAlert, useFriendlyError } from '~/components/member/feedback'
import { ArtHeading } from '~/components/member/Illustration'
import { FormStack, MemberLayout } from '~/components/member/layout'
import { WhyLink } from '~/components/member/WhyLink'
import { useT } from '~/i18n/use-t'
import { useHashParams } from '~/lib/hash'

const MIN_LENGTH = 8
const FORM_ID = 'reset-form'

/**
 * The page behind the reset email (`/reset#t=…`). The token stays in the fragment and is posted only when the button
 * is pressed (R23). Done: the member logs in again (other devices are logged out by the server).
 */
export default function Reset() {
  const { t } = useT()
  const friendly = useFriendlyError()
  const params = useHashParams()
  const token = params.get('t') ?? params.get('token')
  const [password, setPassword] = useState('')
  const [fieldError, setFieldError] = useState<string | null>(null)
  const save = useMutation({ mutationFn: () => api('memberPasswordReset', { body: { token: token!, password } }) })

  const oldLink = !token || (save.isError && isApiError(save.error, 'invalid_link'))
  const weak = save.isError && !oldLink && isWeakPasswordError(save.error)

  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (save.isPending) return
    if ([...password].length < MIN_LENGTH) return setFieldError(t('password.errShort'))
    setFieldError(null)
    save.mutate()
  }

  return (
    <MemberLayout>
      <ArtHeading title={t('reset.title')} art='password' />
      {save.isSuccess ? (
        <>
          <Text role='status'>{t('reset.done')}</Text>
          <ActionBar>
            <Button asChild size='lg' w='full'>
              <RouterLink to='/login'>{t('common.logIn')}</RouterLink>
            </Button>
          </ActionBar>
        </>
      ) : oldLink ? (
        <>
          <Text role='alert'>{t('reset.oldLink')}</Text>
          <ActionBar>
            <Button asChild size='lg' w='full'>
              <RouterLink to='/forgot'>{t('reset.ask')}</RouterLink>
            </Button>
          </ActionBar>
        </>
      ) : (
        <FormStack id={FORM_ID} gap={5} onSubmit={submit} noValidate>
          <PasswordField
            label={t('password.label')}
            value={password}
            onChange={(v) => {
              setPassword(v)
              setFieldError(null)
              if (!save.isPending) save.reset()
            }}
            autoComplete='new-password'
            hint={t('password.hintShort')}
            error={fieldError ?? (weak ? t('password.errWeak') : null)}
          />
          <WhyLink faq='password' label={t('password.tipsTitle')} alignSelf='flex-start' />
          {save.isError && !weak && <ErrorAlert>{friendly(save.error)}</ErrorAlert>}
          <ActionBar>
            <Button type='submit' form={FORM_ID} size='lg' w='full' loading={save.isPending}>
              {t('reset.button')}
            </Button>
          </ActionBar>
        </FormStack>
      )}
    </MemberLayout>
  )
}
